import { Check, ChevronDown, type LucideIcon } from "lucide-react";
import { type KeyboardEvent, useId, useRef, useState } from "react";
import { useDismiss } from "@/shared/hooks/useDismiss";

export type ToolbarSelectOption<T extends string> = { value: T; label: string; icon?: LucideIcon };

/**
 * Escolha única e curta de barra de ferramentas, com a lista desenhada
 * pela plataforma em vez do menu nativo do navegador (2026-10-13, tela
 * de Menções: "quando clica aparece a versão do navegador"). Mesmo
 * contrato de um `<select>` controlado — `value` + `onChange` —, então
 * quem chama não muda o que faz com a escolha.
 *
 * Teclado: Enter/Espaço/↓ abrem; ↑/↓ andam; Enter escolhe; Esc e
 * clique fora fecham (`useDismiss`).
 */
export function ToolbarSelect<T extends string>({
  label,
  prefix,
  icon,
  value,
  options,
  onChange,
  ariaLabel,
  align = "left",
  direction = "down"
}: {
  label?: string;
  /** Rótulo DENTRO do botão ("Ordem: Mais recentes"), no lugar do texto
   *  solto ao lado — pra barras com vários seletores (2026-10-13). */
  prefix?: string;
  /** Ícone antes do rótulo. Se a opção escolhida tiver o próprio, vale
   *  o dela (o "Ver" troca de ícone com o modo). */
  icon?: LucideIcon;
  value: T;
  options: ToolbarSelectOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  align?: "left" | "right";
  /** "up" no pé da página (o seletor de linhas do `Pager`), pra lista
   *  não abrir pra fora da tela. */
  direction?: "down" | "up";
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const current = options.find(option => option.value === value) ?? options[0];
  const TriggerIcon = current?.icon ?? icon;

  const close = () => setOpen(false);
  useDismiss(open, close, { ref: rootRef });

  const openList = () => {
    setActive(Math.max(0, options.findIndex(option => option.value === value)));
    setOpen(true);
  };

  const choose = (next: T) => {
    setOpen(false);
    triggerRef.current?.focus();
    if (next !== value) onChange(next);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!open) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        openList();
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(index => Math.min(options.length - 1, index + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(index => Math.max(0, index - 1));
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose(options[active].value);
    } else if (event.key === "Tab") {
      close();
    }
  };

  return (
    <div className={`toolbar-select${open ? " is-open" : ""}`} ref={rootRef}>
      {label && <span className="toolbar-select-label">{label}</span>}
      <button
        ref={triggerRef}
        type="button"
        className="toolbar-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${ariaLabel}: ${current?.label ?? ""}`}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
      >
        {TriggerIcon && <TriggerIcon size={15} className="toolbar-select-icon" aria-hidden="true" />}
        {prefix && <span className="toolbar-select-prefix">{prefix}:</span>}
        <span className="toolbar-select-value">{current?.label}</span>
        <ChevronDown size={15} aria-hidden="true" />
      </button>
      {open && (
        <ul
          id={listId}
          className={`toolbar-select-list align-${align}${direction === "up" ? " opens-up" : ""}`}
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((option, index) => {
            const selected = option.value === value;
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={selected}
                className={`toolbar-select-option${index === active ? " is-active" : ""}${selected ? " is-selected" : ""}`}
                onMouseEnter={() => setActive(index)}
                onMouseDown={event => event.preventDefault()}
                onClick={() => choose(option.value)}
              >
                <span className="toolbar-select-option-label">
                  {option.icon && <option.icon size={15} aria-hidden="true" />}
                  {option.label}
                </span>
                {selected && <Check size={15} aria-hidden="true" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
